/** @jsxImportSource @emotion/react */
import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useUpdateMediaManagerTemplateMutation } from 'modules/manager/store/media-manager-api';
import { modalButton, modalError } from 'modules/template-editor/ui/components/elements/modals/styles';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import { Button } from 'ud-ui/components/button/styles';
import Modal from 'ud-ui/components/modal';

interface Args {
  groups: MediaManagerTemplateGroup[];
}

/**
 * Both groups refetch by the mutation's tags: the target by its id, the one the template leaves
 * because it provides the tags of its templates (IMGM-1515).
 */
function useMoveTemplateModal({ groups }: Args) {
  const { t } = useLingui();
  const [updateTemplate, { isLoading }] = useUpdateMediaManagerTemplateMutation();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [targetGroupId, setTargetGroupId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(() => groups.filter((group) => group.id !== template?.templateGroupId), [groups, template]);

  const open = useCallback((next: MediaManagerTemplate) => {
    setError(null);
    setTemplate(next);
    setTargetGroupId(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(async () => {
    if (!template || targetGroupId == null) return;
    setError(null);
    try {
      await updateTemplate({ id: template.id, templateGroupId: targetGroupId }).unwrap();
      close();
    } catch (err) {
      console.error('Failed to move media manager template:', err);
      setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось переместить шаблон, попробуйте ещё раз`);
    }
  }, [template, targetGroupId, updateTemplate, close, t]);

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={isLoading ? () => undefined : close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Переместить в группу</Trans>
        </UDText>
        <Modal.Body className="flex flex-col gap-3">
          <label className="flex flex-col gap-2">
            <UDText variant="body-3" className="text-gray-400">
              <Trans>Группа</Trans>
            </UDText>
            <select
              className="h-10 rounded border border-gray-200 px-3"
              value={targetGroupId ?? ''}
              disabled={isLoading}
              onChange={(event) => setTargetGroupId(event.target.value ? Number(event.target.value) : null)}
            >
              <option value="">{t`Выберите группу`}</option>
              {options.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </label>
        </Modal.Body>
        <Modal.Footer justify="start">
          <Button
            type="button"
            className={classNames('flex-center', isLoading && 'loading')}
            disabled={targetGroupId == null || isLoading}
            css={modalButton}
            onClick={() => void onSubmit()}
          >
            <Trans>Переместить</Trans>
          </Button>
          {error && <span css={modalError}>{error}</span>}
        </Modal.Footer>
      </Modal>
    ),
  };
}

export default useMoveTemplateModal;
